/**
 * Manage pagination of child organization integrations.
 *
 * This hook handles the complex logic of:
 * 1. Loading child organizations for a parent org user
 * 2. Building connection rows for each child org
 * 3. Paginating through child orgs to display connections incrementally
 * 4. Refreshing connection data when needed
 *
 * Key optimizations:
 * - Batch fetches last run data for static connections across multiple orgs
 * - Uses Promise.allSettled to handle partial failures gracefully
 * - Buffers rows as needed
 * - Only loads PAGE_SIZE orgs at a time to avoid overwhelming the UI
 *
 * @param {string} organizationCode - Parent organization code
 * @param {Array} providerList - List of available integration providers
 * @param {Object} currentUser - Current user object
 * @param {boolean} hasStaticConnection - Whether user has a static connection
 * @param {Object} providerDetails - Provider details for static connections
 * @returns {Object} Object containing:
 *   - rows: Currently displayed connection rows
 *   - isBuilding: Whether connections are being built
 *   - canLoadMore: Whether more connections can be loaded
 *   - loadNextPage: Function to load the next page of connections
 *   - refreshRows: Function to refresh currently loaded connections
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useContainer } from "@src/contexts/container-context";
import { getConnectionProviderLastRunQueryKey } from "@queries/connection";
import { useChildOrganizations } from "@queries/organization";
import { buildConnectionRowsForOrg } from "@views/integrations/utils";

const PAGE_SIZE = 10; // Number of child orgs to process per page

export function useParentOrgPagination(
  organizationCode,
  providerList,
  currentUser,
  hasStaticConnection,
  providerDetails
) {
  const [childOrgs, setChildOrgs] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [displayedRowCount, setDisplayedRowCount] = useState(0);
  const [isBuilding, setIsBuilding] = useState(false);
  const [nextOrgIndex, setNextOrgIndex] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);

  const queryClient = useQueryClient();
  const { integrationService } = useContainer();

  // Fetch child organizations using React Query hook
  const { data: childOrgsData, isLoading: isLoadingChildOrgs } =
    useChildOrganizations(organizationCode, {
      enabled: !!providerList?.length,
    });

  /**
   * Builds connection rows for a batch of child organizations.
   *
   * Optimization: For static connections, batch fetches last run data for all orgs
   * in one API call instead of individual calls. If batch fetch fails, falls back
   * to individual fetches per org.
   *
   * Uses Promise.allSettled to ensure one org's failure doesn't block others.
   *
   * @param {Array} orgsToBuild - Array of child organization objects
   * @returns {Promise<Array>} Array of connection row objects
   */
  const buildChildOrgRows = useCallback(
    async (orgsToBuild) => {
      let lastRunMapping = {};
      let batchFetchSucceeded = false;

      // Batch fetch last run data for static connections
      if (hasStaticConnection && providerDetails && orgsToBuild.length > 0) {
        try {
          const orgCodes = orgsToBuild.map((o) => o.code);
          // Use React Query's fetchQuery to leverage caching and shared query key logic
          const queryKey = getConnectionProviderLastRunQueryKey(
            providerDetails.providerName,
            orgCodes
          );
          lastRunMapping =
            (await queryClient.fetchQuery({
              queryKey,
              queryFn: () =>
                integrationService.getConnectionProviderLastRun(
                  providerDetails.providerName,
                  orgCodes
                ),
            })) || {};
          batchFetchSucceeded = true;
        } catch (error) {
          console.error(
            "[Integrations] Failed to batch fetch last run data:",
            error
          );
          // Continue with individual fetches - buildConnectionRowsForOrg will handle it
        }
      }

      // Build rows for all orgs in parallel, handling failures gracefully
      const results = await Promise.allSettled(
        orgsToBuild.map((o) => {
          // Use pre-fetched data if available, otherwise let buildConnectionRowsForOrg fetch it
          const preFetchedLastRun =
            hasStaticConnection && batchFetchSucceeded
              ? lastRunMapping[o.code] ?? null // Fetched and either had data or there is no last run
              : undefined; // Not fetched yet, fetch individually
          return buildConnectionRowsForOrg(
            o,
            providerList,
            currentUser,
            preFetchedLastRun,
            queryClient
          );
        })
      );

      // Extract successful results, log failures but continue
      const rows = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          rows.push(...result.value);
        } else {
          console.error(
            `[Integrations] Failed to build rows for org ${orgsToBuild[index]?.code}:`,
            result.reason
          );
        }
      });

      return rows;
    },
    [
      hasStaticConnection,
      providerDetails,
      providerList,
      currentUser,
      queryClient,
      integrationService,
    ]
  );

  /**
   * Loads the next page of connections by processing more child organizations.
   *
   * Strategy:
   * 1. If we already have enough buffered rows, just increase display count
   * 2. Otherwise, process orgs in batches until we have enough rows
   * 3. Continue until we reach target row count or run out of orgs
   *
   * This approach buffers rows as needed.
   */
  const loadNextPage = useCallback(async () => {
    if (childOrgs.length === 0) {
      return;
    }

    try {
      setIsBuilding(true);
      let currentIndex = nextOrgIndex;
      const targetRowCount = displayedRowCount + PAGE_SIZE;
      let accumulatedRows = [...allRows];

      // If we already have enough rows buffered, just update display count
      if (accumulatedRows.length >= targetRowCount) {
        setDisplayedRowCount(targetRowCount);
        setCanLoadMore(
          currentIndex < childOrgs.length ||
            accumulatedRows.length > targetRowCount
        );
        setIsBuilding(false);
        return;
      }

      // Process orgs in batches until we have enough rows
      while (
        accumulatedRows.length < targetRowCount &&
        currentIndex < childOrgs.length
      ) {
        const orgsToRequest = Math.min(
          PAGE_SIZE,
          childOrgs.length - currentIndex
        );
        const batch = childOrgs.slice(
          currentIndex,
          currentIndex + orgsToRequest
        );

        const rows = await buildChildOrgRows(batch);
        accumulatedRows.push(...rows);
        currentIndex += orgsToRequest;
      }

      setAllRows(accumulatedRows);
      setDisplayedRowCount(targetRowCount);
      setNextOrgIndex(currentIndex);
      setCanLoadMore(
        currentIndex < childOrgs.length ||
          accumulatedRows.length > targetRowCount
      );
    } catch (error) {
      console.error(
        "[Integrations] Failed to load parent connections page",
        error
      );
      throw error;
    } finally {
      setIsBuilding(false);
    }
  }, [childOrgs, nextOrgIndex, displayedRowCount, allRows, buildChildOrgRows]);

  const refreshRows = useCallback(async () => {
    setIsBuilding(true);
    try {
      const alreadyLoaded = childOrgs.slice(0, nextOrgIndex);
      const rows = await buildChildOrgRows(alreadyLoaded);

      setAllRows(rows);
      setDisplayedRowCount(Math.min(PAGE_SIZE, rows.length));
      setCanLoadMore(
        nextOrgIndex < childOrgs.length || rows.length > PAGE_SIZE
      );
    } catch (error) {
      console.error(
        "[Integrations] Error refreshing parent connections:",
        error
      );
      throw error;
    } finally {
      setIsBuilding(false);
    }
  }, [childOrgs, nextOrgIndex, buildChildOrgRows]);

  // Update child orgs state when data is fetched
  useEffect(() => {
    if (childOrgsData) {
      const sorted = (childOrgsData || []).slice().sort((a, b) => {
        const an = (a.name || "").toLowerCase();
        const bn = (b.name || "").toLowerCase();
        return an.localeCompare(bn);
      });
      setChildOrgs(sorted);
      setAllRows([]);
      setDisplayedRowCount(0);
      setNextOrgIndex(0);
      setCanLoadMore(sorted.length > 0);
      setIsBuilding(false);
    } else if (isLoadingChildOrgs) {
      setIsBuilding(true);
    } else {
      // No data and not loading - reset state
      setChildOrgs([]);
      setAllRows([]);
      setDisplayedRowCount(0);
      setCanLoadMore(false);
      setIsBuilding(false);
    }
  }, [childOrgsData, isLoadingChildOrgs]);

  useEffect(() => {
    if (childOrgs.length > 0 && nextOrgIndex === 0) {
      loadNextPage();
    }
  }, [childOrgs.length, nextOrgIndex]);

  return {
    rows: allRows.slice(0, displayedRowCount),
    isBuilding,
    canLoadMore,
    loadNextPage,
    refreshRows,
  };
}
